import React from "react";
import { Avatar, Box, Paper, Stack, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartmentOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import type { ScheduledReportTopPerformer } from "@/app2/shared/data";
import { palette, SCHEDULED_REPORTS_DEFAULT_PROFILE_IMAGE_URL } from "@/constants";

export interface ScheduledReportsTopPerformersProps {
  topPerformers: (ScheduledReportTopPerformer | null)[];
}

function hasDisplayValue(value: unknown): boolean {
  if (value == null) return false;
  const s = String(value).trim();
  return s.length > 0 && s !== ".";
}

export const ScheduledReportsTopPerformers: React.FC<ScheduledReportsTopPerformersProps> = ({ topPerformers }) => (
  <Stack spacing={2} sx={{ width: "100%" }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: palette.grey70 }}>
      Top 10 Performers
    </Typography>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
        gap: 2,
        width: "100%",
      }}
    >
      {topPerformers.map((p, idx) => (
        <Paper key={idx} sx={{ p: 2, borderRadius: 2, backgroundColor: "background.paper", minWidth: 0 }}>
          {p ? (
            <Stack direction="column" spacing={2} alignItems="flex-start" textAlign="left">
              <Avatar
                src={p.ProfileImage || SCHEDULED_REPORTS_DEFAULT_PROFILE_IMAGE_URL}
                sx={{ width: 56, height: 56, border: "2px solid", borderColor: "primary.main" }}
              >
                {((p.Name as string) ?? "?")[0]?.toUpperCase()}
              </Avatar>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                }}
                title={(p.Name as string) ?? "Unknown"}
              >
                {(p.Name as string) ?? "Unknown"}
              </Typography>
              <Stack spacing={1} alignItems="flex-start" sx={{ width: "100%" }}>
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <PlayCircleOutlineIcon sx={{ fontSize: 18, color: palette.grey70 }} />
                  <Typography variant="body2" sx={{ color: palette.grey70 }}>{String(p["Completed Lessons This Month"] ?? 0)} lessons this month</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <LocalFireDepartmentOutlinedIcon sx={{ fontSize: 18, color: palette.grey70 }} />
                  <Typography variant="body2" sx={{ color: palette.grey70 }}>{String(p.Streak ?? 0)} day streak</Typography>
                </Stack>
                {(hasDisplayValue(p.Supervisor) || hasDisplayValue(p.Division)) && (
                  <Stack direction="row" alignItems="flex-start" gap={0.5}>
                    <InfoOutlinedIcon sx={{ fontSize: 18, mt: 0.25, color: palette.grey70 }} />
                    <Stack spacing={0.25}>
                      {hasDisplayValue(p.Supervisor) && (
                        <Typography variant="body2" sx={{ color: palette.grey70 }}>Supervisor: {String(p.Supervisor).trim()}</Typography>
                      )}
                      {hasDisplayValue(p.Division) && (
                        <Typography variant="body2" sx={{ color: palette.grey70 }}>Division: {String(p.Division).trim()}</Typography>
                      )}
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </Stack>
          ) : (
            <Stack direction="column" spacing={2} alignItems="flex-start" textAlign="left">
              <Avatar sx={{ width: 56, height: 56, bgcolor: "grey.700" }} />
              <Typography variant="subtitle1" color="text.disabled">
                No performer yet
              </Typography>
              <Typography variant="body2" color="text.disabled">
                —
              </Typography>
            </Stack>
          )}
        </Paper>
      ))}
    </Box>
  </Stack>
);
